/**
 * Registriert den Offline-Cache aus public/sw.js — nur im Produktions-Build.
 * Im Dev-Server (`npm run dev`) würde ein Service Worker alte, per HMR
 * längst überholte Module aus dem Cache servieren und für verwirrende
 * "warum sehe ich meine Änderung nicht"-Momente sorgen.
 */
type UpdateListener = () => void;

const updateListener = new Set<UpdateListener>();
let wartenderServiceWorker: ServiceWorker | null = null;
let nachAktivierungNeuLaden = false;

function meldeUpdate(serviceWorker: ServiceWorker): void {
	wartenderServiceWorker = serviceWorker;
	updateListener.forEach((listener) => listener());
}

/** Informiert die Oberfläche, sobald eine neue App-Version einsatzbereit ist. */
export function beiAppUpdate(listener: UpdateListener): () => void {
	updateListener.add(listener);
	if (wartenderServiceWorker) listener();
	return () => updateListener.delete(listener);
}

/** Aktiviert die wartende Version; nach dem Controllerwechsel wird neu geladen. */
export function aktiviereAppUpdate(): void {
	if (!wartenderServiceWorker) return;
	nachAktivierungNeuLaden = true;
	wartenderServiceWorker.postMessage({ typ: 'UPDATE_AKTIVIEREN' });
}

export function registriereServiceWorker(): void {
	if (!import.meta.env.PROD) return;
	if (!('serviceWorker' in navigator)) return;

	window.addEventListener('load', () => {
		navigator.serviceWorker
			// Der Worker liegt unter /app/ und darf auch nur dort greifen — die
			// Landingpage auf / soll er nicht kontrollieren oder cachen.
			.register('/app/sw.js', { scope: '/app/' })
			.then((registrierung) => {
				if (registrierung.waiting && navigator.serviceWorker.controller) {
					meldeUpdate(registrierung.waiting);
				}

				registrierung.addEventListener('updatefound', () => {
					const installation = registrierung.installing;
					if (!installation) return;
					installation.addEventListener('statechange', () => {
						if (installation.state === 'installed' && navigator.serviceWorker.controller) {
							meldeUpdate(installation);
						}
					});
				});

				// Mobile Browser bleiben oft tagelang offen. Beim Zurückkehren
				// wird deshalb aktiv nach einer neuen Version gefragt.
				document.addEventListener('visibilitychange', () => {
					if (document.visibilityState === 'visible') void registrierung.update();
				});
			})
			.catch(() => {
				// Kein Service Worker heißt nur: kein Offline-App-Start beim
				// nächsten Mal. Die Protokoll-Daten bleiben trotzdem sicher.
			});
	});

	navigator.serviceWorker.addEventListener('controllerchange', () => {
		if (!nachAktivierungNeuLaden) return;
		nachAktivierungNeuLaden = false;
		window.location.reload();
	});
}
